import React, { useContext, useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    useToast,
    Card,
    CardBody,
    Flex,
    SimpleGrid,
    Spinner,
    Button,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    useBreakpointValue,
    Badge,
    Icon,
    Divider,
    Wrap,
    WrapItem,
} from '@chakra-ui/react';
import { FaSync, FaChartLine, FaExclamationTriangle, FaUsers, FaShieldAlt, FaBrain, FaUserFriends, FaBoxOpen, FaClock, FaCheckCircle } from 'react-icons/fa';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie,
    Legend,
    LineChart,
    Line,
} from 'recharts';
import AuthContext from '../../context/AuthContext';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

const BIAS_CODES = {
    OVERCONF: 'Overconfidence',
    LOSSAV: 'Loss Aversion',
    ANCHOR: 'Anchoring',
    HERD: 'Herd Behavior',
    FAMILIAR: 'Familiarity Bias',
    CONFIRM: 'Confirmation Bias',
    RECENCY: 'Recency Bias',
    AFFECT: 'Affect Infusion',
    MENTAL: 'Mental Accounting',
    IMPACT: 'Impact Bias',
    DISPOS: 'Disposition Effect',
    COGNITIVE: 'Cognitive Dissonance',
    TAXAV: 'Tax Aversion',
    REGRET: 'Regret Aversion',
    EMOTION: 'Emotional State',
};

// Professional color palette matching app theme
const AGE_COLORS = ['#0B2545', '#38A169', '#805AD5', '#DD6B20', '#E53E3E', '#D69E2E'];
const SEGMENT_COLORS = ['#0B2545', '#38A169', '#805AD5', '#DD6B20', '#E53E3E', '#D69E2E', '#00B5D8', '#718096'];

function DashboardRMCustomerInsights() {
    const { token } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [insightsData, setInsightsData] = useState(null);
    const toast = useToast();

    const isMobile = useBreakpointValue({ base: true, md: false });
    const statSize = useBreakpointValue({ base: 'sm', md: 'md' });

    const fetchInsightsData = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_BASE_URL}/content/rm-customer-insights`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setInsightsData(res.data);
        } catch (error) {
            console.error('Insights fetch error:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to load customer insights.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
            setInsightsData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInsightsData();
        // eslint-disable-next-line
    }, []);

    const renderTopBiases = () => {
        if (!insightsData?.top_biases_in_my_portfolio || insightsData.top_biases_in_my_portfolio.length === 0) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Text color="gray.500">No bias data available.</Text>
                </Flex>
            );
        }

        const biasData = insightsData.top_biases_in_my_portfolio.map((bias) => ({
            name: BIAS_CODES[bias.bias_code] || bias.bias_name,
            score: parseFloat(bias.mean_score.toFixed(2)),
            customers: bias.affected_customers,
        }));

        return (
            <ResponsiveContainer width="100%" height={350}>
                <LineChart data={biasData} margin={{ top: 20, right: 30, left: 20, bottom: 70 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis
                        dataKey="name"
                        stroke="#4A5568"
                        angle={-45}
                        textAnchor="end"
                        height={90}
                        tick={{ fontSize: isMobile ? 8 : 10 }}
                    />
                    <YAxis domain={[0, 100]} stroke="#4A5568" />
                    <RechartsTooltip
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                        formatter={(value, name, props) => [
                            `Score: ${value}, Affected: ${props.payload.customers} customers`,
                            'Bias Severity'
                        ]}
                    />
                    <Line
                        type="monotone"
                        dataKey="score"
                        stroke="#0B2545"
                        strokeWidth={3}
                        dot={{ fill: '#0B2545', r: 5 }}
                        activeDot={{ r: 7 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        );
    };

    const renderAgeDistribution = () => {
        if (!insightsData?.age_distribution || insightsData.age_distribution.length === 0) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Text color="gray.500">No age distribution data available.</Text>
                </Flex>
            );
        }

        const ageData = insightsData.age_distribution.map((item, index) => ({
            name: item.bucket,
            value: item.count,
            percentage: item.percentage,
            color: AGE_COLORS[index % AGE_COLORS.length],
        }));

        return (
            <Box>
                <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                        <Pie
                            data={ageData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            dataKey="value"
                        >
                            {ageData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Pie>
                        <RechartsTooltip
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                            formatter={(value, name, props) => [
                                `${value} (${props.payload.percentage.toFixed(1)}%)`,
                                props.payload.name
                            ]}
                        />
                        <Legend
                            verticalAlign="bottom"
                            height={36}
                            wrapperStyle={{ fontSize: '12px' }}
                        />
                    </PieChart>
                </ResponsiveContainer>

                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} mt={4}>
                    {ageData.map((item, index) => (
                        <Card key={index} bg="gray.50" borderRadius="md" boxShadow="sm" borderWidth="1px" borderColor="gray.200">
                            <CardBody p={3}>
                                <VStack spacing={1} align="start">
                                    <HStack>
                                        <Box w="10px" h="10px" bg={item.color} borderRadius="sm" />
                                        <Text fontSize="xs" fontWeight="bold" color="gray.700">
                                            {item.name}
                                        </Text>
                                    </HStack>
                                    <Text fontSize="lg" fontWeight="bold" color="gray.700">
                                        {item.value}
                                    </Text>
                                    <Text fontSize="xs" color="gray.600">
                                        {item.percentage.toFixed(1)}%
                                    </Text>
                                </VStack>
                            </CardBody>
                        </Card>
                    ))}
                </SimpleGrid>
            </Box>
        );
    };

    const renderSegmentsCards = () => {
        if (!insightsData?.segments_distribution || insightsData.segments_distribution.length === 0) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Text color="gray.500">No segment data available.</Text>
                </Flex>
            );
        }

        const segmentData = insightsData.segments_distribution
            .sort((a, b) => b.count - a.count)
            .map((item, index) => ({
                name: item.segment,
                count: item.count,
                percentage: item.percentage,
                color: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
            }));

        return (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                {segmentData.map((segment, index) => (
                    <Card
                        key={index}
                        bg="white"
                        borderRadius="lg"
                        boxShadow="md"
                        borderWidth="2px"
                        borderColor={segment.color}
                        transition="all 0.2s"
                        _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                    >
                        <CardBody>
                            <VStack align="start" spacing={2}>
                                <Badge colorScheme="blue" fontSize="xs" mb={1}>
                                    Segment
                                </Badge>
                                <Heading size="sm" color="gray.700" noOfLines={2} minH="40px">
                                    {segment.name}
                                </Heading>
                                <Divider />
                                <HStack justify="space-between" w="100%">
                                    <VStack align="start" spacing={0}>
                                        <Text fontSize="2xl" fontWeight="bold" color={segment.color}>
                                            {segment.count}
                                        </Text>
                                        <Text fontSize="xs" color="gray.600">
                                            customers
                                        </Text>
                                    </VStack>
                                    <Badge 
                                        colorScheme="gray" 
                                        fontSize="lg" 
                                        px={3} 
                                        py={1}
                                        bg={segment.color}
                                        color="white"
                                    >
                                        {segment.percentage.toFixed(1)}%
                                    </Badge>
                                </HStack>
                            </VStack>
                        </CardBody>
                    </Card>
                ))}
            </SimpleGrid>
        );
    };

    const renderSegmentsChart = () => {
        if (!insightsData?.segments_distribution || insightsData.segments_distribution.length === 0) {
            return null;
        }

        const segmentData = insightsData.segments_distribution
            .sort((a, b) => b.count - a.count)
            .map((item, index) => ({
                name: item.segment,
                count: item.count,
                percentage: item.percentage,
                color: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
            }));

        return (
            <ResponsiveContainer width="100%" height={350}>
                <BarChart data={segmentData} margin={{ top: 20, right: 30, left: 20, bottom: 70 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis
                        dataKey="name"
                        stroke="#4A5568"
                        angle={-45}
                        textAnchor="end"
                        height={90}
                        tick={{ fontSize: isMobile ? 8 : 10 }}
                    />
                    <YAxis stroke="#4A5568" />
                    <RechartsTooltip
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                        formatter={(value, name, props) => [
                            `${value} customers (${props.payload.percentage.toFixed(1)}%)`,
                            'Count'
                        ]}
                    />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                        {segmentData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        );
    };

    const renderPortfolioProductRecommendations = () => {
        if (!insightsData?.portfolio_product_recommendations || insightsData.portfolio_product_recommendations.length === 0) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Text color="gray.500">No product recommendations available.</Text>
                </Flex>
            );
        }

        return (
            <VStack spacing={4} align="stretch">
                {insightsData.portfolio_product_recommendations.map((product, index) => (
                    <Card
                        key={index}
                        bg="white"
                        borderRadius="lg"
                        boxShadow="md"
                        borderWidth="2px"
                        borderColor="brand.teal"
                        transition="all 0.2s"
                        _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}
                    >
                        <CardBody>
                            <VStack spacing={4} align="stretch">
                                {/* Product Header */}
                                <Flex justify="space-between" align="start" flexWrap="wrap" gap={2}>
                                    <VStack align="start" spacing={1} flex={1}>
                                        <Heading size={{ base: 'sm', md: 'md' }} color="brand.navy">
                                            {product.product_type}
                                        </Heading>
                                        <Badge colorScheme="blue" fontSize="sm">
                                            {product.product_code}
                                        </Badge>
                                    </VStack>
                                    <Badge colorScheme="green" fontSize="lg" px={4} py={2}>
                                        {(product.avg_confidence * 100).toFixed(0)}% Confidence
                                    </Badge>
                                </Flex>

                                <Divider />

                                {/* Product Stats */}
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                    <Card bg="#E8F4FF" borderRadius="md" borderWidth="1px" borderColor="brand.teal">
                                        <CardBody p={4}>
                                            <Stat>
                                                <StatLabel fontSize="sm" color="gray.600">
                                                    Recommended For
                                                </StatLabel>
                                                <StatNumber fontSize="2xl" color="brand.navy">
                                                    {product.recommended_for_count} Customers
                                                </StatNumber>
                                            </Stat>
                                        </CardBody>
                                    </Card>

                                    <Card bg="#E8F5E9" borderRadius="md" borderWidth="1px" borderColor="#38A169">
                                        <CardBody p={4}>
                                            <Stat>
                                                <StatLabel fontSize="sm" color="gray.600">
                                                    Average Confidence
                                                </StatLabel>
                                                <StatNumber fontSize="2xl" color="#38A169">
                                                    {(product.avg_confidence * 100).toFixed(1)}%
                                                </StatNumber>
                                            </Stat>
                                        </CardBody>
                                    </Card>
                                </SimpleGrid>

                                {/* Biases Addressed */}
                                {product.top_biases_addressed && product.top_biases_addressed.length > 0 && (
                                    <Box>
                                        <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={2}>
                                            Top Biases Addressed:
                                        </Text>
                                        <Wrap spacing={2}>
                                            {product.top_biases_addressed.map((bias, biasIndex) => (
                                                <WrapItem key={biasIndex}>
                                                    <Badge
                                                        colorScheme="blue"
                                                        fontSize="sm"
                                                        px={3}
                                                        py={1}
                                                        borderRadius="full"
                                                    >
                                                        {bias.replace(/_/g, ' ')}
                                                    </Badge>
                                                </WrapItem>
                                            ))}
                                        </Wrap>
                                    </Box>
                                )}
                            </VStack>
                        </CardBody>
                    </Card>
                ))}
            </VStack>
        );
    };

    const summary = insightsData?.summary;
    const attention = insightsData?.attention_needed;

    return (
        <Box minH="100vh" width="100%" pb={{ base: '90px', md: '20px' }}>
            <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
                <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                    {/* Header */}
                    <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={4}>
                        <Box flex="1">
                            <Heading
                                size={{ base: 'lg', md: 'xl' }}
                                color="brand.navy"
                                mb={2}
                                fontWeight="bold"
                            >
                                <HStack spacing={3}>
                                    <Icon as={FaChartLine} boxSize={{ base: 6, md: 8 }} />
                                    <Text>My Portfolio Insights</Text>
                                </HStack>
                            </Heading>
                            <Text color="gray.600" fontSize="sm">
                                Comprehensive view of your customer portfolio
                            </Text>
                        </Box>
                        <Button
                            leftIcon={<FaSync />}
                            colorScheme="blue"
                            bg="brand.navy"
                            size={{ base: 'sm', md: 'md' }}
                            onClick={fetchInsightsData}
                            isLoading={loading}
                            _hover={{ bg: 'brand.navy2' }}
                        >
                            Refresh
                        </Button>
                    </Flex>

                    {loading && (
                        <Flex justify="center" align="center" py={20}>
                            <VStack spacing={4}>
                                <Spinner size="xl" color="brand.navy" thickness="4px" />
                                <Text color="gray.500">Loading insights...</Text>
                            </VStack>
                        </Flex>
                    )}

                    {!loading && insightsData && (
                        <>
                            {/* Summary Statistics */}
                            {summary && (
                                <>
                                    <Heading size="md" color="brand.navy" mb={2}>
                                        Summary Statistics
                                    </Heading>
                                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 3, md: 4 }}>
                                        <Card bg="white" borderRadius="xl" boxShadow="md" border="1px" borderColor="gray.200">
                                            <CardBody p={{ base: 4, md: 5 }}>
                                                <Stat size={statSize}>
                                                    <HStack spacing={2} mb={1}>
                                                        <Icon as={FaUsers} color="brand.navy" boxSize={{ base: 4, md: 5 }} />
                                                        <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                                                            Total Customers
                                                        </StatLabel>
                                                    </HStack>
                                                    <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="brand.navy" fontWeight="bold">
                                                        {summary.total_customers}
                                                    </StatNumber>
                                                </Stat>
                                            </CardBody>
                                        </Card>

                                        <Card bg="white" borderRadius="xl" boxShadow="md" border="1px" borderColor="gray.200">
                                            <CardBody p={{ base: 4, md: 5 }}>
                                                <Stat size={statSize}>
                                                    <HStack spacing={2} mb={1}>
                                                        <Icon as={FaExclamationTriangle} color="#E53E3E" boxSize={{ base: 4, md: 5 }} />
                                                        <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                                                            High Risk
                                                        </StatLabel>
                                                    </HStack>
                                                    <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="#E53E3E" fontWeight="bold">
                                                        {summary.high_risk_count}
                                                    </StatNumber>
                                                </Stat>
                                            </CardBody>
                                        </Card>

                                        <Card bg="white" borderRadius="xl" boxShadow="md" border="1px" borderColor="gray.200">
                                            <CardBody p={{ base: 4, md: 5 }}>
                                                <Stat size={statSize}>
                                                    <HStack spacing={2} mb={1}>
                                                        <Icon as={FaShieldAlt} color="#DD6B20" boxSize={{ base: 4, md: 5 }} />
                                                        <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                                                            Medium Risk
                                                        </StatLabel>
                                                    </HStack>
                                                    <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="#DD6B20" fontWeight="bold">
                                                        {summary.medium_risk_count}
                                                    </StatNumber>
                                                </Stat>
                                            </CardBody>
                                        </Card>

                                        <Card bg="white" borderRadius="xl" boxShadow="md" border="1px" borderColor="gray.200">
                                            <CardBody p={{ base: 4, md: 5 }}>
                                                <Stat size={statSize}>
                                                    <HStack spacing={2} mb={1}>
                                                        <Icon as={FaCheckCircle} color="#38A169" boxSize={{ base: 4, md: 5 }} />
                                                        <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                                                            Low Risk
                                                        </StatLabel>
                                                    </HStack>
                                                    <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="#38A169" fontWeight="bold">
                                                        {summary.low_risk_count}
                                                    </StatNumber>
                                                </Stat>
                                            </CardBody>
                                        </Card>
                                    </SimpleGrid>

                                    {/* Urgency Metrics */}
                                    <Heading size="sm" color="brand.navy" mt={4} mb={2}>
                                        Urgency Levels
                                    </Heading>
                                    <SimpleGrid columns={{ base: 2, md: summary.critical_urgency_count === null || summary.critical_urgency_count === undefined ? 3 : 4 }} spacing={{ base: 3, md: 4 }}>
                                        {summary.critical_urgency_count !== null && summary.critical_urgency_count !== undefined && (
                                        <Card bg="#FEE2E2" borderRadius="xl" boxShadow="sm" border="1px" borderColor="#E53E3E">
                                            <CardBody p={{ base: 4, md: 5 }}>
                                                <Stat size={statSize}>
                                                    <HStack spacing={2} mb={1}>
                                                        <Icon as={FaClock} color="#E53E3E" boxSize={{ base: 4, md: 5 }} />
                                                        <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.700">
                                                            Critical
                                                        </StatLabel>
                                                    </HStack>
                                                    <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="#E53E3E" fontWeight="bold">
                                                        {summary.critical_urgency_count}
                                                    </StatNumber>
                                                </Stat>
                                            </CardBody>
                                        </Card>
                                        )}

                                        <Card bg="#FED7AA" borderRadius="xl" boxShadow="sm" border="1px" borderColor="#DD6B20">
                                            <CardBody p={{ base: 4, md: 5 }}>
                                                <Stat size={statSize}>
                                                    <HStack spacing={2} mb={1}>
                                                        <Icon as={FaClock} color="#DD6B20" boxSize={{ base: 4, md: 5 }} />
                                                        <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.700">
                                                            High
                                                        </StatLabel>
                                                    </HStack>
                                                    <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="#DD6B20" fontWeight="bold">
                                                        {summary.high_urgency_count}
                                                    </StatNumber>
                                                </Stat>
                                            </CardBody>
                                        </Card>

                                        <Card bg="#FEF3C7" borderRadius="xl" boxShadow="sm" border="1px" borderColor="#D69E2E">
                                            <CardBody p={{ base: 4, md: 5 }}>
                                                <Stat size={statSize}>
                                                    <HStack spacing={2} mb={1}>
                                                        <Icon as={FaClock} color="#D69E2E" boxSize={{ base: 4, md: 5 }} />
                                                        <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.700">
                                                            Medium
                                                        </StatLabel>
                                                    </HStack>
                                                    <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="#D69E2E" fontWeight="bold">
                                                        {summary.medium_urgency_count}
                                                    </StatNumber>
                                                </Stat>
                                            </CardBody>
                                        </Card>

                                        <Card bg="#D1FAE5" borderRadius="xl" boxShadow="sm" border="1px" borderColor="#38A169">
                                            <CardBody p={{ base: 4, md: 5 }}>
                                                <Stat size={statSize}>
                                                    <HStack spacing={2} mb={1}>
                                                        <Icon as={FaClock} color="#38A169" boxSize={{ base: 4, md: 5 }} />
                                                        <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.700">
                                                            Low
                                                        </StatLabel>
                                                    </HStack>
                                                    <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="#38A169" fontWeight="bold">
                                                        {summary.low_urgency_count}
                                                    </StatNumber>
                                                </Stat>
                                            </CardBody>
                                        </Card>
                                    </SimpleGrid>

                                    {/* Average Priority Score */}
                                    <Card bg="#E8F4FF" borderRadius="xl" boxShadow="md" border="1px" borderColor="brand.teal">
                                        <CardBody p={{ base: 4, md: 5 }}>
                                            <Stat size={statSize}>
                                                <HStack spacing={2} mb={1}>
                                                    <Icon as={FaChartLine} color="brand.navy" boxSize={{ base: 4, md: 5 }} />
                                                    <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                                                        Average Priority Score
                                                    </StatLabel>
                                                </HStack>
                                                <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="brand.navy" fontWeight="bold">
                                                    {summary.average_priority_score?.toFixed(2)}
                                                </StatNumber>
                                                <StatHelpText>Portfolio average</StatHelpText>
                                            </Stat>
                                        </CardBody>
                                    </Card>
                                </>
                            )}

                            {/* Attention Needed */}
                            {attention && (
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 3, md: 4 }}>
                                    <Card bg="#FED7AA" borderRadius="xl" boxShadow="sm" border="1px" borderColor="#DD6B20">
                                        <CardBody p={{ base: 4, md: 5 }}>
                                            <Stat size={statSize}>
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.700">
                                                    High Priority Customers
                                                </StatLabel>
                                                <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="#DD6B20" fontWeight="bold">
                                                    {attention.high_priority_count}
                                                </StatNumber>
                                                <StatHelpText>Needs immediate attention</StatHelpText>
                                            </Stat>
                                        </CardBody>
                                    </Card>

                                    <Card bg="gray.50" borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.300">
                                        <CardBody p={{ base: 4, md: 5 }}>
                                            <Stat size={statSize}>
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                                                    Customers Without Scores
                                                </StatLabel>
                                                <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="gray.600" fontWeight="bold">
                                                    {attention.no_scores_count}
                                                </StatNumber>
                                                <StatHelpText>Pending analysis</StatHelpText>
                                            </Stat>
                                        </CardBody>
                                    </Card>
                                </SimpleGrid>
                            )}

                            {/* Portfolio Product Recommendations */}
                            {insightsData.portfolio_product_recommendations && insightsData.portfolio_product_recommendations.length > 0 && (
                                <Card bg="white" borderRadius="lg" boxShadow="md">
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaBoxOpen} color="brand.navy" boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color="brand.navy">
                                                Portfolio Product Recommendations
                                            </Heading>
                                        </HStack>
                                        <Text fontSize="sm" color="gray.600" mb={4}>
                                            Products recommended for your portfolio based on customer behavioral profiles
                                        </Text>
                                        {renderPortfolioProductRecommendations()}
                                    </CardBody>
                                </Card>
                            )}

                            {/* Top Biases */}
                            <Card bg="white" borderRadius="lg" boxShadow="md">
                                <CardBody>
                                    <HStack spacing={2} mb={4}>
                                        <Icon as={FaBrain} color="brand.navy" boxSize={6} />
                                        <Heading size={{ base: 'sm', md: 'md' }} color="brand.navy">
                                            Top Biases in My Portfolio
                                        </Heading>
                                    </HStack>
                                    <Text fontSize="sm" color="gray.600" mb={4}>
                                        Top 5 behavioral biases affecting your customers
                                    </Text>
                                    {renderTopBiases()}
                                </CardBody>
                            </Card>

                            {/* Age Distribution */}
                            <Card bg="white" borderRadius="lg" boxShadow="md">
                                <CardBody>
                                    <HStack spacing={2} mb={4}>
                                        <Icon as={FaUsers} color="brand.navy" boxSize={6} />
                                        <Heading size={{ base: 'sm', md: 'md' }} color="brand.navy">
                                            Age Distribution
                                        </Heading>
                                    </HStack>
                                    {renderAgeDistribution()}
                                </CardBody>
                            </Card>

                            {/* Behavioral Segments - Cards */}
                            <Card bg="white" borderRadius="lg" boxShadow="md">
                                <CardBody>
                                    <HStack spacing={2} mb={4}>
                                        <Icon as={FaUserFriends} color="brand.navy" boxSize={6} />
                                        <Heading size={{ base: 'sm', md: 'md' }} color="brand.navy">
                                            Behavioral Segments
                                        </Heading>
                                    </HStack>
                                    <Text fontSize="sm" color="gray.600" mb={4}>
                                        Distribution of customers across behavioral segments
                                    </Text>
                                    {renderSegmentsCards()}
                                </CardBody>
                            </Card>

                            {/* Segments Distribution Chart */}
                            <Card bg="white" borderRadius="lg" boxShadow="md">
                                <CardBody>
                                    <Heading size={{ base: 'sm', md: 'md' }} mb={4} color="brand.navy">
                                        Segments Distribution Chart
                                    </Heading>
                                    {renderSegmentsChart()}
                                </CardBody>
                            </Card>
                        </>
                    )}

                    {!loading && !insightsData && (
                        <Card bg="white" borderRadius="lg" boxShadow="md">
                            <CardBody>
                                <VStack py={10} spacing={4}>
                                    <Text fontSize="lg" color="gray.500" fontWeight="medium">
                                        No insights data available
                                    </Text>
                                    <Text fontSize="sm" color="gray.400">
                                        Click refresh to load your portfolio insights
                                    </Text>
                                </VStack>
                            </CardBody>
                        </Card>
                    )}
                </VStack>
            </Container>
        </Box>
    );
}

export default DashboardRMCustomerInsights;